import { Button } from '@fukuri-admins/ui/common/Button';
import { Link } from '@fukuri-admins/ui/common/Link';
import { Stack } from '@fukuri-admins/ui/common/Stack';
import { ROUTES } from '~/utils/routes';
import styles from './styles.module.scss';

type Props = {
  /**
   * ダイアログタイトル
   */
  title: string;
  /**
   * 利用人数（従業員数）。未設定の場合は省略
   */
  employeeCount?: number;
  /**
   * 送付先住所
   */
  shippingAddress: {
    /** 郵便番号（フォーマット済み、例: "100-0001"） */
    postalCode: string;
    /** 都道府県名 */
    prefecture: string;
    /** 市区町村 */
    city: string;
    /** 番地以降の住所 */
    streetAddress: string;
  };
  /**
   * 確定処理実行中かどうか
   */
  isConfirming: boolean;
  /**
   * 「この内容で確定」ボタン押下時のコールバック
   */
  onConfirm: React.ComponentProps<typeof Button>['onClick'];
  /**
   * 「内容を修正」ボタン押下時のコールバック（編集ビューへ切り替える）
   */
  onEdit: React.ComponentProps<typeof Button>['onClick'];
};

/**
 * 送付先住所確認ダイアログの Presentation
 *
 * 利用人数と送付先住所を読み取り専用で表示し、確認を促すダイアログ（確認ビュー）。
 * 確認が完了するまで閉じられない（ESC で閉じない・× ボタンなし・オーバーレイクリックで閉じない）。
 * 「内容を修正」で Container が編集ビュー（./edit）に切り替える。
 *
 * @see https://www.figma.com/design/IrJmlTnzuyJOuXtlizSLwB/%F0%9F%9A%80Sandbox?node-id=27873-81233
 */
export const ShippingConfirmationDialogPresentation: React.FC<Props> = ({
  title,
  employeeCount,
  shippingAddress,
  isConfirming,
  onConfirm,
  onEdit,
}) => {
  return (
    <div className={styles.overlay}>
      <div
        className={styles.dialog}
        role='dialog'
        aria-modal='true'
        aria-labelledby='shipping-confirmation-dialog-title'
      >
        <Stack direction='column' gap={8}>
          <h2 className={styles.title} id='shipping-confirmation-dialog-title'>
            {title}
          </h2>
          <p className={styles.description}>
            利用人数と送付先を確認してください。期日までに修正がない場合、この内容で登録案内カードを発行します。
          </p>
        </Stack>

        <Stack direction='column' gap={16} className={styles.content}>
          <Stack direction='row' gap={24} alignItems='flex-start'>
            <div className={styles.label}>利用人数</div>
            <div className={styles.value}>{employeeCount ?? '未設定'}</div>
          </Stack>

          <Stack direction='row' gap={24} alignItems='flex-start'>
            <div className={styles.label}>送付先</div>
            <Stack direction='column' gap={8} className={styles.addressValue}>
              <p>{shippingAddress.postalCode}</p>
              <p>
                {shippingAddress.prefecture}
                {shippingAddress.city}
              </p>
              <p>{shippingAddress.streetAddress}</p>
            </Stack>
          </Stack>
        </Stack>

        <Stack direction='row' gap={8} alignItems='center' justifyContent='space-between'>
          <Link
            to={ROUTES.CONTACT}
            isExternal={true}
            target='_blank'
            rel='noopener noreferrer'
            className={styles.contactLink}
          >
            発行内容について問い合わせる
          </Link>
          <Stack direction='row' gap={8} alignItems='center'>
            <Button variant='outline' onClick={onEdit} disabled={isConfirming}>
              内容を修正
            </Button>
            <Button variant='primary' onClick={onConfirm} disabled={isConfirming}>
              この内容で確定
            </Button>
          </Stack>
        </Stack>
      </div>
    </div>
  );
};
